import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner, toast } from '@heroui/react';
import QRCode from 'qrcode';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { trpc } from '@/lib/trpc';
import wepayIconUrl from '@/assets/icons/wepay.svg';

export type WepayModalCheckout = {
  paymentId: string;
  codeUrl: string;
  amount: number;
  productName?: string;
};

interface WepayModalProps {
  open: boolean;
  checkout: WepayModalCheckout | null;
  onCancel: () => void;
  onPaid: () => void;
}

function formatMoney(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export function WepayModal({ open, checkout, onCancel, onPaid }: WepayModalProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const paidHandledRef = useRef(false);

  const utils = trpc.useUtils();

  const { data: statusData } = trpc.bizOrder.getPaymentStatus.useQuery(
    { paymentId: checkout?.paymentId ?? '' },
    {
      enabled: open && Boolean(checkout?.paymentId),
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        if (status === 'SUCCESS' || status === 'FAILED' || status === 'CANCELED' || status === 'CANCELLED') {
          return false;
        }
        return 2500;
      },
    }
  );

  useEffect(() => {
    if (!open) {
      paidHandledRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !checkout?.codeUrl) {
      setQrDataUrl(null);
      setQrError(null);
      return;
    }

    let cancelled = false;
    setQrError(null);
    QRCode.toDataURL(checkout.codeUrl, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: 'M',
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setQrDataUrl(null);
          setQrError('二维码生成失败');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, checkout?.codeUrl]);

  useEffect(() => {
    if (
      !open ||
      paidHandledRef.current ||
      (statusData?.status !== 'SUCCESS' && statusData?.orderStatus !== 'PAID')
    )
      return;
    paidHandledRef.current = true;
    toast.success('支付成功');
    void utils.bizProduct.getByType.invalidate({ type: 'PLAN' });
    onPaid();
  }, [open, statusData?.status, onPaid, utils.bizProduct.getByType]);

  const handleOpenChange = (next: boolean) => {
    if (!next) onCancel();
  };

  const handleRefresh = async () => {
    if (!checkout?.paymentId) return;
    try {
      const result = await utils.bizOrder.getPaymentStatus.fetch({
        paymentId: checkout.paymentId,
      });
      if (result.status === 'SUCCESS' || result.orderStatus === 'PAID') {
        if (!paidHandledRef.current) {
          paidHandledRef.current = true;
          toast.success('支付成功');
          void utils.bizProduct.getByType.invalidate({ type: 'PLAN' });
          onPaid();
        }
        return;
      }
      toast.success('尚未检测到支付结果，请稍后再试');
    } catch {
      toast.danger('刷新支付状态失败');
    }
  };

  const amount = checkout?.amount ?? 0;

  return (
    <Modal isOpen={open} onOpenChange={handleOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" size="sm">
          <Modal.Dialog className="max-w-[360px]">
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading className="text-xl font-semibold text-foreground">
                请扫码支付
              </Modal.Heading>
            </Modal.Header>

            <Modal.Body className="flex flex-col items-center gap-4 px-6 pb-2 pt-1">
              <div className="flex items-center gap-2">
                <img src={wepayIconUrl} alt="" className="size-7 shrink-0" />
                <span className="text-base font-medium text-foreground">微信支付</span>
              </div>

              <p className="text-sm text-default-700">
                扫码支付{' '}
                <span className="font-semibold text-[#E05A2B]">{formatMoney(amount)}</span> 元
              </p>

              <div className="flex size-[240px] items-center justify-center rounded-lg border border-default-200 bg-white p-3">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="微信支付二维码" className="size-full object-contain" />
                ) : qrError ? (
                  <span className="px-4 text-center text-sm text-danger">{qrError}</span>
                ) : (
                  <Spinner size="lg" />
                )}
              </div>

              <p className="text-xs text-default-500">
                已支付暂未跳转？{' '}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-foreground"
                  onClick={() => void handleRefresh()}
                >
                  刷新
                </button>
              </p>
            </Modal.Body>

            <Modal.Footer className="justify-center">
              <Button type="button" variant="ghost" onPress={onCancel}>
                关闭
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
